
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatDate(dateString: string): string {
  const options: Intl.DateTimeFormatOptions = { 
    weekday: 'short', 
    month: 'short', 
    day: 'numeric' 
  };
  
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', options);
}

export function formatTime(timeString: string): string {
  // Assuming timeString format is "HH:MM"
  const [hours, minutes] = timeString.split(':').map(Number);
  
  // Convert to 12-hour format
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export function formatTimeAgo(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) {
    return 'just now';
  }
  
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  }
  
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function convertToLocalTime(date: string, time: string): string {
  // Create a date object from the game date and time
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = time.split(':').map(Number);
  
  // JavaScript months are 0-indexed
  const gameDateTime = new Date(year, month - 1, day, hours, minutes);
  
  // Format the time in the user's local timezone
  return gameDateTime.toLocaleTimeString([], { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });
}

export function getUserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Unknown';
  } catch (error) {
    console.error('Error getting timezone:', error);
    return 'Unknown';
  }
}

function parseUtcDatetime(utcDatetime: string | undefined): Date | null {
  if (!utcDatetime) return null;

  try {
    const date = new Date(utcDatetime);

    if (isNaN(date.getTime())) return null;

    return date;
  } catch (error) {
    console.error('Error parsing UTC datetime:', error);
    return null;
  }
}

export function formatUtcToLocalDate(utcDatetime: string | undefined): string | null {
  const date = parseUtcDatetime(utcDatetime);
  if (!date) return null;

  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
}

export function formatUtcToLocalTime(utcDatetime: string | undefined): string | null {
  const date = parseUtcDatetime(utcDatetime);
  if (!date) return null;

  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}
